import * as React from 'react';

import { subYears } from 'date-fns';

import DayPickerInput from 'react-day-picker/DayPickerInput';
import { IAge } from '../../interfaces/IRequestForm';
import MomentLocaleUtils from 'react-day-picker/moment';
import { __ } from '../../helpers/TranslationService';
import useOnclickOutside from 'react-cool-onclickoutside';

export interface IDayInputPickerComponent {
    index: number;
    elementRef?: any;
    childAge: IAge;
    minAdultAge: number;
    error?: boolean;
    onChange: Function;
    departureDate: number;
}

export const PickerOverlay = ({ classNames, children, hideOverlay, ...props }) => {
    const refOverlay = useOnclickOutside(() => {
        hideOverlay();
    });

    return (
        <div ref={refOverlay} className={classNames.overlayWrapper} {...props}>
            <div className={classNames.overlay}>{children}</div>
        </div>
    );
};

/**
 * <DayPickerInput keepFocus={false} fixes Firefox issue with <YearMonthForm /> selects being autoclosed
 * Along with custom <PickerOverlay /> component which manages clicks outside to hide calendar overlay
 * https://github.com/gpbl/react-day-picker/issues/599
 * https://github.com/gpbl/react-day-picker/issues/579
 */
export const DayInputPickerComponent = (props: IDayInputPickerComponent): any => {
    const { index, childAge, onChange, error, elementRef, departureDate } = props;

    const dayPickerRef = React.useRef(null);

    const datepickerProps = {
        min: subYears(new Date(departureDate * 1000), 25),
        max: new Date(),
        placeholder: __('DD.MM.YYYY', 'dreamlines'),
    };

    const [pickerDate, setPickerDate] = React.useState(childAge.birthday ?? new Date());

    const handleYearMonthChange = (date: Date): void => {
        setPickerDate(date);
        // restore daypicker input focus after year or month change
        // so that click outside will close calendar overlay
        dayPickerRef.current?.input?.focus();
    };

    if (elementRef?.current !== null && error) {
        elementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    React.useEffect(() => {
        if (pickerDate === childAge.birthday && childAge.birthday) {
            setPickerDate(childAge.birthday);
        }
    }, [childAge.birthday]);

    return (
        <DayPickerInput
            data-testid={`request-form-date-input-${index}`}
            keepFocus={false}
            ref={dayPickerRef}
            overlayComponent={(props) => (
                <PickerOverlay
                    {...props}
                    hideOverlay={() => dayPickerRef.current?.hideDayPicker()}
                />
            )}
            dayPickerProps={{
                modifiers: {
                    disabled: [
                        {
                            before: datepickerProps.min,
                            after: datepickerProps.max,
                        },
                    ],
                },
                month: pickerDate,
                onMonthChange: handleYearMonthChange,
                captionElement: () => {
                    return (
                        <YearMonthForm
                            date={pickerDate}
                            localeUtils={MomentLocaleUtils}
                            minDate={datepickerProps.min}
                            maxDate={datepickerProps.max}
                            onChange={handleYearMonthChange}
                        />
                    );
                },
            }}
            onDayPickerHide={() => childAge.birthday && setPickerDate(childAge.birthday)}
            format="DD.MM.YYYY"
            inputProps={{
                readOnly: true,
                required: true,
                style: { width: '100%' },
                className: error ? 'error' : '',
            }}
            formatDate={MomentLocaleUtils.formatDate}
            parseDate={MomentLocaleUtils.parseDate}
            value={childAge.birthday}
            placeholder={datepickerProps.placeholder}
            onDayChange={(date) => onChange(index, new Date(date))}
        />
    );
};

export interface IYearMonthForm {
    date: Date;
    minDate: Date;
    maxDate: Date;
    localeUtils: any;
    onChange: Function;
}

const YearMonthForm = (props: IYearMonthForm) => {
    const { date, minDate, maxDate, localeUtils, onChange } = props;

    const minDateYear = minDate.getFullYear();
    const maxDateYear = maxDate.getFullYear();

    let years = [];
    for (let i = minDateYear; i <= maxDateYear; i++) {
        years.push(i);
    }
    const months = localeUtils.getMonths();
    const handleChange = function handleChange(e) {
        const { year, month } = e.target.form;
        onChange(new Date(year.value, month.value));
    };

    return (
        <form className="DayPicker-Caption">
            <div className="DayPicker-RequestFormCaption">
                <select name="month" onChange={handleChange} value={date.getMonth()}>
                    {months.map((month, i) => (
                        <option key={month} value={i}>
                            {month}
                        </option>
                    ))}
                </select>
                <select name="year" onChange={handleChange} value={date.getFullYear()}>
                    {years.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>
        </form>
    );
};
